import {
  Box,
  Typography,
  useTheme,
  Switch,
  CircularProgress,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import BarChartIcon from '@mui/icons-material/BarChart'
import React, { FC, useState } from 'react'
import { RequestEventDashboardGraph } from '../../components/molecules/RequestEventDashboardGraph'
import { BackButton } from '../../components/molecules/BackButton'
import { useRequestEventStatisticsQuery } from '../../features/usageStats'

type DataItem = {
  label: number
  eventCount: number
}

type StatisticsMode = 'ocr' | 'jahis'

// 統計パネルコンポーネント（コンポーネント外に定義）
const StatisticPanel: FC<{ label: string; value: number; unit: string }> = ({
  label,
  value,
  unit,
}) => {
  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: 1,
        p: 2,
        textAlign: 'center',
        bgcolor: 'white',
        height: 'calc(100% - 2px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        margin: '1px',
      }}
    >
      <Typography variant="h4">{label}</Typography>
      <Typography variant="h2" sx={{ my: 2 }}>
        {value.toLocaleString()}
        <span style={{ fontSize: '0.5em', marginLeft: '0.25em' }}>{unit}</span>
      </Typography>
    </Box>
  )
}

/**
 * リクエストイベント統計ページ（TanStack Query版）
 */
export const RequestEventStatisticsPage: FC = () => {
  const { data: statistics, isPending } = useRequestEventStatisticsQuery()
  const [mode, setMode] = useState<StatisticsMode>('ocr')
  const theme = useTheme()
  const [pharmacyNameWidth, setPharmacyNameWidth] = useState<number>(205)

  // モード切替処理
  const handleModeToggle = () => {
    setMode((prevMode) => (prevMode === 'ocr' ? 'jahis' : 'ocr'))
  }

  // 日ごとのデータ変換
  const getDailyData = (): DataItem[] => {
    if (!statistics) return []

    const dailyData =
      mode === 'ocr'
        ? statistics.daily_event_count_dicts || []
        : statistics.daily_jahis_count_dicts || []

    // 配列の順序をそのまま維持し、index順にラベルを生成して表示する
    return dailyData.map((item) => ({
      label: item.label, // APIからのラベルを使用
      eventCount: item.count, // count値をeventCountとして設定
    }))
  }

  // 時間ごとのデータ変換
  const getHourlyData = (): DataItem[] => {
    if (!statistics) return []

    const hourlyData =
      mode === 'ocr'
        ? statistics.hourly_event_count_dicts || []
        : statistics.hourly_jahis_count_dicts || []

    // 配列の順序をそのまま維持し、index順にラベルを生成して表示する
    return hourlyData.map((item) => ({
      label: item.label, // APIからのラベルを使用
      eventCount: item.count, // count値をeventCountとして設定
    }))
  }

  const dailyData = getDailyData()
  const hourlyData = getHourlyData()

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        py: 4,
        px: { xs: 2, sm: 4 },
        overflow: 'auto',
        maxWidth: '100vw',
        boxSizing: 'border-box',
      }}
    >
      {isPending && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {/* 薬局名パネル */}
      <Box
        ref={(element: HTMLDivElement | null) => {
          if (element) {
            const width = element.offsetWidth
            if (width > 0 && width !== pharmacyNameWidth) {
              setPharmacyNameWidth(width)
            }
          }
        }}
        sx={{
          position: 'fixed',
          zIndex: 1300,
          left: 0,
          top: 0,
          bgcolor: 'rgba(66, 66, 66, 0.75)',
          color: '#fff',
          p: 0.8,
          height: '36px',
          borderRadius: '0px 0px 8px 0px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(2px)',
          paddingRight: '12px',
        }}
      >
        <BarChartIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
        <Typography variant="body2">
          {statistics?.pharmacy_name
            ? `${statistics.pharmacy_name} - 利用状況`
            : ''}
        </Typography>
      </Box>
      {/* OCR/JAHISモード切替スライダー（薬局名パネルに隣接して配置） */}
      <Box
        sx={{
          position: 'fixed',
          zIndex: 1300,
          top: 0,
          color: '#fff',
          p: 0.8,
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          // 薬局名パネルの幅に応じて動的に配置
          left: `${pharmacyNameWidth + 8}px`,
        }}
      >
        <Typography
          sx={[
            {
              mr: 1,
              fontSize: '0.75rem',
            },
            mode === 'ocr'
              ? {
                  color: theme.palette.primary.dark,
                }
              : {
                  color: theme.palette.secondary.dark,
                },
            mode === 'ocr'
              ? {
                  fontWeight: 'bold',
                }
              : {
                  fontWeight: 'normal',
                },
          ]}
        >
          OCR件数
        </Typography>
        <Switch
          checked={mode === 'jahis'}
          onChange={handleModeToggle}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase': {
              color: '#ffffff',
              '&.Mui-checked': {
                color: '#ffffff',
                '& + .MuiSwitch-track': {
                  backgroundColor: theme.palette.secondary.dark,
                  opacity: 1,
                },
              },
            },
            '& .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.dark,
              opacity: 1,
            },
            '& .MuiSwitch-thumb': {
              boxShadow: '0 2px 3px rgba(0,0,0,0.3)',
            },
          }}
        />
        <Typography
          sx={[
            {
              ml: 1,
              fontSize: '0.75rem',
            },
            mode === 'jahis'
              ? {
                  color: theme.palette.secondary.dark,
                }
              : {
                  color: theme.palette.secondary.main,
                },
            mode === 'jahis'
              ? {
                  fontWeight: 'bold',
                }
              : {
                  fontWeight: 'normal',
                },
          ]}
        >
          総レセコン送付件数
        </Typography>
      </Box>
      <Box sx={{ mt: 5, mb: 10, maxWidth: '100%', overflow: 'hidden' }}>
        <Grid
          container
          spacing={4}
          sx={{ height: 'auto', width: '100%', mx: 0, pr: 2 }}
        >
          {/* 左側の統計情報 */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ mb: 5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: 5,
                pb: 2,
              }}
            >
              {/* 統計値表示 */}
              <Box sx={{ flex: '1 0 auto', minHeight: '120px' }}>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    bgcolor: 'white',
                    height: 'calc(100% - 2px)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    margin: '1px',
                  }}
                >
                  <Typography variant="h4">{`年間累計 ${
                    statistics
                      ? `(${statistics.yearly_event_start_date}-${statistics.today_date})`
                      : ''
                  }`}</Typography>
                  <Typography variant="h4" sx={{ my: 2 }}>
                    <span>＜準備中＞</span>
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: '1 0 auto', minHeight: '120px' }}>
                <StatisticPanel
                  label={`月間累計 ${
                    statistics
                      ? `(${statistics.start_date_of_this_month}-${statistics.today_date})`
                      : ''
                  }`}
                  value={
                    mode === 'ocr'
                      ? statistics?.monthly_event_count || 0
                      : statistics?.monthly_jahis_count || 0
                  }
                  unit="件"
                />
              </Box>
              <Box sx={{ flex: '1 0 auto', minHeight: '120px', mb: 3 }}>
                <StatisticPanel
                  label={`本日累計 ${
                    statistics ? `(${statistics.today_date})` : ''
                  }`}
                  value={
                    mode === 'ocr'
                      ? statistics?.daily_event_count || 0
                      : statistics?.daily_jahis_count || 0
                  }
                  unit="件"
                />
              </Box>
            </Box>
          </Grid>

          {/* 右側のグラフ表示 */}
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxSizing: 'border-box',
              pr: { xs: 0, md: 2 },
              pb: 2,
            }}
          >
            {/* 上部グラフ：日ごとの */}
            <Box
              sx={{
                height: '380px',
                mb: 5,
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              {dailyData.length > 0 && (
                <RequestEventDashboardGraph
                  data={dailyData}
                  title={`日別 ${
                    statistics
                      ? `(${statistics.the_date_after_one_month_ago}-${statistics.today_date})`
                      : ''
                  }`}
                  xTitle="日付"
                  color={
                    mode === 'ocr'
                      ? theme.palette.primary.dark
                      : theme.palette.secondary.dark
                  }
                />
              )}
            </Box>

            {/* 下部グラフ：時間ごとの */}
            <Box
              sx={{
                height: '380px',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
                mt: 'auto',
              }}
            >
              {hourlyData.length > 0 && (
                <RequestEventDashboardGraph
                  data={hourlyData}
                  title={`時間別 ${
                    statistics
                      ? `(${statistics.the_date_after_one_month_ago}-${statistics.today_date})`
                      : ''
                  }`}
                  xTitle="時間"
                  color={
                    mode === 'ocr'
                      ? theme.palette.primary.dark
                      : theme.palette.secondary.dark
                  }
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <BackButton text="管理ポータルに戻る" />
    </Box>
  )
}
